import { type FC, type ReactNode, useRef } from "react";

import { clsx } from "clsx";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";

import { type ParagraphRun } from "./paragraph_editor_runs";
import { $seed } from "./paragraph_editor_state";
import { ParagraphFormatBar } from "./paragraph_format_bar";
import { ParagraphRulesPlugin } from "./paragraph_rules_plugin";

// The format bar's height and its gap to the paragraph.
const FORMAT_BAR_ROOM = 50;

const THEME = {
  paragraph: "",
  text: { bold: "font-bold", italic: "italic", underline: "underline" },
};

// How his paragraph looks in the Report, for the editor to look the same.
export type ParagraphLook = {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  color: string;
  height: number;
};

// One paragraph of his Word draft, edited in place over the Report: fixed on
// the paragraph (`spot`, in the window), the same size and look (`look`), its
// format bar floating above. `line` sits under it (an error).
export const ParagraphEditor: FC<{
  spot: { top: number; left: number; width: number; room: number };
  look: ParagraphLook;
  caret: number;
  runs: ReadonlyArray<ParagraphRun>;
  saving: boolean;
  active: boolean;
  outside: ReadonlyArray<Document>;
  line?: ReactNode;
  onDone(runs: ParagraphRun[]): void;
  onCancel(): void;
}> = ({
  spot,
  look,
  caret,
  runs,
  saving,
  active,
  outside,
  line,
  onDone,
  onCancel,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const closed = useRef(false);

  return (
    <LexicalComposer
      initialConfig={{
        namespace: "ParagraphEditor",
        theme: THEME,
        editorState: () => $seed(runs),
        onError: (error) => {
          throw error;
        },
      }}
    >
      <div
        ref={container}
        role="dialog"
        aria-label="Edit paragraph"
        className="fixed z-30"
        style={{ top: spot.top, left: spot.left, width: spot.width }}
      >
        {/* Over the paragraph, right-aligned; under it when the text above would be covered. */}
        <div
          className={clsx(
            "absolute right-0 w-max",
            spot.room < FORMAT_BAR_ROOM ? "top-full mt-2" : "bottom-full mb-2",
          )}
        >
          <ParagraphFormatBar saving={saving} closed={closed} onDone={onDone} />
        </div>
        <div className="bg-white outline-2 outline-offset-2 outline-teal-500">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-label="Paragraph text"
                className="whitespace-pre-wrap outline-none"
                style={{
                  fontFamily: look.fontFamily,
                  fontSize: look.fontSize,
                  lineHeight: look.lineHeight,
                  color: look.color,
                  minHeight: look.height,
                }}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {line}
        </div>
        <HistoryPlugin />
        <ParagraphRulesPlugin
          caret={caret}
          outside={outside}
          active={active}
          container={container}
          closed={closed}
          onDone={onDone}
          onCancel={onCancel}
        />
      </div>
    </LexicalComposer>
  );
};
